import React from 'react';
import { MAX_SLOTS, slotsError } from './model/time';
import TdButton from './Button';
import TimeField from './TimeField';

const DAY = 24 * 60;
const toMin = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

/**
 * 마지막 구간의 끝이 첫 구간의 시작을 넘지 않게 — 첫 시작부터 하루(24시간) 안에서 끝나야 한다.
 * 넘는 끝 시각이면 첫 시작 시각으로 바꾼다. 구간이 하나면 그대로.
 */
export const limitLastEnd = (slots, to) => {
  if (slots.length < 2) return to;
  const first = toMin(slots[0].from);
  const from = toMin(slots[slots.length - 1].from);
  const start = (from - first + DAY) % DAY;
  const length = (toMin(to) - from + DAY) % DAY;
  return start + length > DAY ? slots[0].from : to;
};

/**
 * 작업 시간 구간 여러 개 입력 (예: 10:00–12:00, 15:00–20:00) — 신청 폼 · Pre-join · Profile 공용.
 * 구간마다 시작 · 끝 + ×, 아래 "+ Add time range" (최대 5개). 본인 시간대 기준이다.
 * 시각은 직접 쳐서 고치거나 (분 단위 아무 값) 10분 단위 목록에서 고른다 (TimeField).
 *
 * @param slots    [{ from, to }]
 * @param onChange (next) => void
 * @param fieldClassName 시각 칸 크기
 * @param overnight 끝이 시작보다 이르면 다음 날로 본다 (Pre-join · Profile). false 면 같은 날 안에서만 (신청 폼)
 *   · 다음 날로 넘어가도 마지막 구간의 끝은 첫 구간의 시작을 넘지 못한다 (limitLastEnd)
 */
const HoursRanges = ({
  slots,
  onChange,
  fieldClassName,
  disabled,
  overnight = true,
  fromLabel = 'Start',
  toLabel = 'End',
  addLabel = '+ Add time range',
  removeLabel = 'Remove time range',
}) => {
  // 마지막 구간 끝은 첫 시작을 넘지 않게 맞춘다 — 첫 시작 · 마지막 시작을 바꿀 때도
  const fit = (list) => {
    if (!overnight || list.length < 2) return list;
    const last = list[list.length - 1];
    const to = limitLastEnd(list, last.to);
    return to === last.to ? list : [...list.slice(0, -1), { ...last, to }];
  };
  const set = (i, patch) => onChange(fit(slots.map((x, j) => (j === i ? { ...x, ...patch } : x))));
  const add = () => {
    // 마지막 구간 끝 1시간 뒤에서 시작하는 2시간 구간을 기본으로 (23:50 을 넘기지 않는다)
    const last = slots[slots.length - 1];
    const [h, m] = (last?.to ?? '09:00').split(':').map(Number);
    const at = (min) => {
      const v = Math.min(min, 23 * 60 + 50);
      return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
    };
    const start = h * 60 + m + 60;
    onChange(fit([...slots, { from: at(start), to: at(start + 120) }]));
  };
  return (
    <div className='flex flex-col gap-2'>
      {slots.map((x, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className='flex items-center gap-2 text-[14px] text-text-secondary'>
          <TimeField
            aria-label={`${fromLabel} ${i + 1}`}
            value={x.from}
            disabled={disabled}
            onChange={(v) => set(i, { from: v })}
            className={fieldClassName}
          />
          <span className='shrink-0'>–</span>
          <TimeField
            aria-label={`${toLabel} ${i + 1}`}
            value={x.to}
            disabled={disabled}
            onChange={(v) => set(i, { to: v })}
            limit={
              overnight && i === slots.length - 1 ? (t) => limitLastEnd(slots, t) : undefined
            }
            className={fieldClassName}
          />
          {/* 지우기 — 끝 시각 입력칸 바로 오른쪽 */}
          {slots.length > 1 && !disabled && (
            <button
              type='button'
              aria-label={`${removeLabel} ${i + 1}`}
              onClick={() => onChange(slots.filter((_, j) => j !== i))}
              className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[18px] text-text-secondary hover:bg-green-deep03/[0.06] hover:text-text-primary'
            >
              ×
            </button>
          )}
          {overnight && x.to < x.from && (
            <span className='shrink-0 text-[12px] text-text-secondary'>next day</span>
          )}
        </div>
      ))}
      {!disabled && slots.length < MAX_SLOTS && (
        <TdButton
          variant='ghost'
          size='s'
          onClick={add}
          className='w-fit border-dashed border-green-deep03/40 px-3 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
        >
          {addLabel}
        </TdButton>
      )}
    </div>
  );
};

export { slotsError };

export default HoursRanges;
